import {
	Avatar,
	Badge,
	Button,
	Flex,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	MenuProps,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Switch,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Text,
	useDisclosure,
} from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import { useEffect, useMemo, useRef } from "react";
import DataTable, {
	Cell,
	SearchBar,
} from "../components/table/ServerSideTable";
import { useEnterprises } from "../hooks/enterprises.hooks";
import { Enterprise } from "../types/enterprises.types";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { useCentersPaginated } from "../hooks/centers.hooks";
import { Center } from "../types/center.types";
import { useAskForRegistrationUsers, useUsers } from "../hooks/user.hooks";
import Address from "../components/Address";
import PersistingTabIndex, {
	PersistingTabIndexHandler,
} from "../components/PersistingTabIndex";
import AddUserIcon from "../components/icons/AddUserIcon";
import SimpleModal from "../components/modals/SimpleModal";
import { useTryLaterToast } from "../hooks/toast.hooks";
import { useMutation, useQueryClient } from "react-query";
import { updateUser } from "../queries/user.queries";
import { AddIcon } from "@chakra-ui/icons";
import EnterpriseForm, {
	EnterpriseFormHandler,
} from "../components/forms/EnterpriseForm";
import {
	archiveEnterpriseToggle,
	createEnterprise,
	enterpriseToggleSleepMode,
} from "../mutations/enterprise.mutations";
import CenterForm, { CenterFormHandler } from "../components/forms/CenterForm";
import {
	archiveCenterToggle,
	centerToggleSleepMode,
	createCenter,
} from "../mutations/center.mutations";
import AddUserIconGhost from "../components/icons/AddUserIconGhost";
import MoreIcon from "../components/icons/MoreIcon";
import UserForm from "../components/forms/UserForm";
import { User } from "../types/user.types";
import EnterpriseModal from "../components/modals/EnterpriseModal";
import CenterModal from "../components/modals/CenterModal";
import { MutateErrors, mutateOnError } from "../lib/generic";
import { fPhoneNumber } from "../lib/format";
import { useDataTableContext } from "../hooks/data-table.hooks";

function ContactPageEnterprisesTabMoreMenu({
	enterprise,
	...props
}: { enterprise?: Enterprise | null } & Omit<MenuProps, "children">) {
	const queryClient = useQueryClient();
	const updateEnterpriseModal = useDisclosure();
	const { mutate: mutateEnterSleepMode } = useMutation(
		enterpriseToggleSleepMode,
		{
			onSuccess: () => {
				queryClient.invalidateQueries("enterprises");
			},
			onError: (err: MutateErrors) => {
				queryClient.invalidateQueries("enterprises");
				mutateOnError(err);
			},
		},
	);
	const { mutate: archiveEnterprise } = useMutation(archiveEnterpriseToggle, {
		onSuccess: () => {
			queryClient.invalidateQueries("enterprises");
		},
		onError: (err: MutateErrors) => {
			queryClient.invalidateQueries("enterprises");
			mutateOnError(err);
		},
	});

	return (
		<>
			<Menu {...props}>
				<MenuButton as={Button} variant="outlined" transition="all 0.2s">
					<MoreIcon w="24px" h="24px" />
				</MenuButton>
				<MenuList>
					<MenuItem
						lineHeight={"24px"}
						onClick={() => updateEnterpriseModal.onOpen()}
					>
						Modifier l'entreprise
					</MenuItem>
					{enterprise && (
						<EnterpriseModal
							id={enterprise.id}
							isOpen={updateEnterpriseModal.isOpen}
							onOpen={updateEnterpriseModal.onOpen}
							onClose={updateEnterpriseModal.onClose}
						></EnterpriseModal>
					)}
					{enterprise &&
						enterprise.sleepModeAt !== null &&
						(enterprise.isArchived ? (
							<>
								<MenuItem
									lineHeight={"24px"}
									onClick={() =>
										archiveEnterprise({
											id: enterprise.id,
											isArchived: false,
										})
									}
								>
									Désarchiver l'entreprise
								</MenuItem>
							</>
						) : (
							<>
								<MenuItem
									lineHeight={"24px"}
									onClick={() =>
										mutateEnterSleepMode({
											id: enterprise.id,
											sleep: false,
											reason: "",
										})
									}
								>
									Activer l'entreprise
								</MenuItem>
								<MenuItem
									lineHeight={"24px"}
									onClick={() =>
										archiveEnterprise({
											id: enterprise.id,
											isArchived: true,
										})
									}
								>
									Archiver l'entreprise
								</MenuItem>
							</>
						))}
					{enterprise && enterprise?.sleepModeAt === null && (
						<MenuItem
							lineHeight={"24px"}
							color="red.500"
							onClick={() =>
								mutateEnterSleepMode({
									id: enterprise.id,
									sleep: true,
									reason: "",
								})
							}
						>
							Désactiver l'entreprise
						</MenuItem>
					)}
				</MenuList>
			</Menu>
		</>
	);
}

const OrdersTableFilters = () => {
	const { filters, setFilters } = useDataTableContext();
	useEffect(() => {
		if (filters?.isArchived === undefined && setFilters)
			setFilters("isArchived", "false");
	}, [filters, setFilters]);

	return (
		<Flex gap={4} alignItems={"center"}>
			<Switch
				isChecked={filters?.isArchived === "true"}
				onChange={(evt) => {
					if (setFilters) {
						setFilters("isArchived", evt.target.checked ? "true" : "false");
					}
				}}
			/>
			<span> Entitées archivées</span>
		</Flex>
	);
};

function ContactPageEnterprisesTab() {
	const columns = useMemo<ColumnDef<Enterprise>[]>(
		() => [
			{
				accessorFn: (row) => row.id,
				id: "id",
				header: "ID",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<Text textTransform={"capitalize"}>{row.name}</Text>
				),
				id: "name",
				header: "Nom",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => fPhoneNumber(row.phoneNumber ?? ""),
				id: "phone_number",
				header: "Téléphone",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => <Address id={row.addressId}></Address>,
				id: "address",
				header: "Adresse",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => format(row.createdAt, "dd/MM/yyyy"),
				id: "created_at",
				header: "Date de création",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<>{row.sleepModeAt === null ? "Activé" : "Désactivé"}</>
				),
				id: "enabled",
				header: "Status",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<ContactPageEnterprisesTabMoreMenu enterprise={row} />
				),
				id: "more",
				header: "",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
		],
		[],
	);

	return (
		<>
			<DataTable
				columns={columns}
				paginatedQuery={useEnterprises}
				w={["8rem", "12rem", "8rem", "20rem", "200px", "200px", "40px", "0px"]}
				flexGrow={[0, 2, 0, 1, 0, 0, 0, 0]}
				defaultSortBy={[
					{
						id: "created_at",
						desc: true,
					},
				]}
				cellLink={(cell: Cell) => {
					if (cell.id.endsWith("more")) {
						return;
					}
					return `/contacts/enterprises/${cell.row.id}`;
				}}
				toolbar={
					<Flex my="1rem" justifyContent="space-between">
						<SearchBar />
						<OrdersTableFilters />
					</Flex>
				}
			></DataTable>
		</>
	);
}

function ContactPageCentersTabMoreMenu({
	center,
	...props
}: { center?: Center | null } & Omit<MenuProps, "children">) {
	const queryClient = useQueryClient();
	const updateCenterModal = useDisclosure();
	const { mutate: mutateEnterSleepMode } = useMutation(centerToggleSleepMode, {
		onSuccess: () => {
			queryClient.invalidateQueries("centers");
		},
		onError: (err: MutateErrors) => {
			queryClient.invalidateQueries("centers");
			mutateOnError(err);
		},
	});
	const { mutate: mutateArchiveCenter } = useMutation(archiveCenterToggle, {
		onSuccess: () => {
			queryClient.invalidateQueries("centers");
		},
		onError: (err: MutateErrors) => {
			queryClient.invalidateQueries("centers");
			mutateOnError(err);
		},
	});

	return (
		<>
			<Menu {...props}>
				<MenuButton as={Button} variant="outlined" transition="all 0.2s">
					<MoreIcon w="24px" h="24px" />
				</MenuButton>
				<MenuList>
					<MenuItem
						lineHeight={"24px"}
						onClick={() => updateCenterModal.onOpen()}
					>
						Modifier le center
					</MenuItem>
					{center && (
						<CenterModal
							id={center.id}
							isOpen={updateCenterModal.isOpen}
							onOpen={updateCenterModal.onOpen}
							onClose={updateCenterModal.onClose}
						></CenterModal>
					)}
					{center && center?.sleepModeAt === null && (
						<MenuItem
							lineHeight={"24px"}
							color="red.500"
							onClick={() =>
								mutateEnterSleepMode({
									id: center.id,
									reason: "",
								})
							}
						>
							Désactiver le centre
						</MenuItem>
					)}
					{center &&
						center.sleepModeAt !== null &&
						(!center.isArchived ? (
							<>
								<MenuItem
									lineHeight={"24px"}
									onClick={() =>
										mutateEnterSleepMode({
											id: center.id,
											reason: "",
										})
									}
								>
									Activer le centre
								</MenuItem>
								<MenuItem
									lineHeight={"24px"}
									onClick={() =>
										mutateArchiveCenter({
											id: center.id,
											isArchived: true,
										})
									}
								>
									Archiver le center
								</MenuItem>
							</>
						) : (
							<MenuItem
								lineHeight={"24px"}
								onClick={() =>
									mutateArchiveCenter({
										id: center.id,
										isArchived: false,
									})
								}
							>
								Désarchiver le centre
							</MenuItem>
						))}
				</MenuList>
			</Menu>
		</>
	);
}

function ContactPageCentersTab() {
	const columns = useMemo<ColumnDef<Center>[]>(
		() => [
			{
				accessorFn: (row) => row.id,
				id: "id",
				header: "ID",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => row.name,
				id: "name",
				header: "Nom du centre",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => fPhoneNumber(row.phoneNumber ?? ""),
				id: "phone_number",
				header: "Téléphone",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => <Address id={row.shippingAddressId}></Address>,
				id: "address",
				header: "Adresse",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => row.email,
				id: "email",
				header: "Email",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => <>{format(row.createdAt, "dd/MM/yyyy")}</>,
				id: "created_at",
				header: "Date de création",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<>{row.sleepModeAt === null ? "Activé" : "Désactivé"}</>
				),
				id: "enabled",
				header: "Status",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => <ContactPageCentersTabMoreMenu center={row} />,
				id: "more",
				header: "",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
		],
		[],
	);
	return (
		<DataTable
			columns={columns}
			paginatedQuery={useCentersPaginated}
			w={["8rem", "12rem", "10rem", "30rem", "30rem", "200px", "200px", "40px"]}
			flexGrow={[0, 2, 0, 1, 0, 0, 0, 0]}
			defaultSortBy={[
				{
					id: "created_at",
					desc: true,
				},
			]}
			cellLink={(cell: Cell) => {
				if (cell.id.endsWith("more")) {
					return;
				}
				return `/contacts/centers/${cell.row.id}`;
			}}
			toolbar={
				<Flex my="1rem" justifyContent="space-between">
					<SearchBar />
					<OrdersTableFilters />
				</Flex>
			}
		></DataTable>
	);
}

function ContactPageUsersTabMoreMenu({
	user,
	...props
}: { user: User } & Omit<MenuProps, "children">) {
	const queryClient = useQueryClient();
	const { mutate: mutateUpdateUser } = useMutation(updateUser, {
		onSuccess: () => {
			queryClient.invalidateQueries("users");
		},
	});
	const handleActivateClick = () => {
		mutateUpdateUser({ id: user.id, activatedAt: new Date() });
	};
	const handleDesactivateClick = () => {
		mutateUpdateUser({ id: user.id, activatedAt: null });
	};
	const { onOpen, onClose, isOpen } = useDisclosure();

	return (
		<>
			<Menu {...props}>
				<MenuButton as={Button} variant="outlined" transition="all 0.2s">
					<MoreIcon w="24px" h="24px" />
				</MenuButton>
				<MenuList>
					<MenuItem lineHeight={"24px"} onClick={() => onOpen()}>
						Modifier les informations de l'utilisateur
					</MenuItem>
					{user.activatedAt === null && (
						<MenuItem lineHeight={"24px"} onClick={handleActivateClick}>
							Activer le compte
						</MenuItem>
					)}
					{user.activatedAt !== null && (
						<MenuItem
							lineHeight={"24px"}
							onClick={handleDesactivateClick}
							color="red.500"
						>
							Désactiver le compte
						</MenuItem>
					)}
				</MenuList>
			</Menu>
			{/* Update user information modal */}
			<Modal isOpen={isOpen} onClose={onClose} isCentered size={"xl"}>
				<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6.9px)" />
				<ModalContent>
					<ModalHeader>Modifier les informations de l'utilisateur</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<UserForm
							user={user}
							onSuccess={() => {
								queryClient.invalidateQueries("users");
								onClose();
							}}
						></UserForm>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}

function ContactPageUsersTab() {
	const columns = useMemo<ColumnDef<User>[]>(
		() => [
			{
				accessorFn: (row) => row.id,
				id: "id",
				header: "ID",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<Flex textTransform="capitalize" alignItems="center" h="44px">
						<Avatar
							name={`${row.firstName} ${row.lastName}`}
							h="40px"
							w="40px"
							mr="20px"
						></Avatar>
						{row.firstName} {row.lastName}
					</Flex>
				),
				id: "full_name",
				header: "Utilisateur",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => row.email,
				id: "email",
				header: "Email",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<>{row.activatedAt !== null ? "Activé" : "Désactivé"}</>
				),
				id: "enabled",
				header: "Status",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => <ContactPageUsersTabMoreMenu user={row} />,
				id: "actions",
				header: "",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
		],
		[],
	);
	return (
		<DataTable
			columns={columns}
			paginatedQuery={useUsers}
			w={["8rem", "20rem", "30rem", "200px", "2rem"]}
			flexGrow={[0, 0, 1, 0, 0]}
			defaultSortBy={[
				{
					id: "created_at",
					desc: true,
				},
			]}
			cellLink={(cell) => {
				if (cell.id.endsWith("actions")) {
					return;
				}
				return `/contacts/users/${cell.row.id}`;
			}}
		></DataTable>
	);
}

const AskForRegistrationTable = () => {
	const queryClient = useQueryClient();
	const { tryLater } = useTryLaterToast();
	const { mutate: mutateUpdateUser } = useMutation(updateUser, {
		onSuccess: () => {
			queryClient.invalidateQueries("users");
			queryClient.invalidateQueries("users-registration");
		},
	});
	const columns = useMemo<ColumnDef<User>[]>(
		() => [
			{
				accessorFn: (row) => (
					<Flex textTransform="capitalize" alignItems="center" h="44px">
						<Avatar
							name={`${row.firstName} ${row.lastName}`}
							h="40px"
							w="40px"
							mr="20px"
						></Avatar>
						{row.firstName} {row.lastName}
					</Flex>
				),
				id: "full_name",
				header: "Utilisateur",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<div style={{ display: "flex", flexDirection: "row", gap: "1rem" }}>
						<Button
							variant="link"
							color="brand.700"
							onClick={() => {
								mutateUpdateUser(
									{ id: row.id, activatedAt: new Date() },
									{
										onError: () => {
											tryLater();
										},
									},
								);
							}}
						>
							Accepter
						</Button>
						<Button
							variant="link"
							color="brand.700"
							onClick={() => {
								mutateUpdateUser(
									{ id: row.id, rejectedAt: new Date() },
									{
										onError: () => {
											tryLater();
										},
									},
								);
							}}
						>
							Rejeter
						</Button>
					</div>
				),
				id: "actions",
				header: "Actions",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
		],
		[],
	);
	return (
		<DataTable
			columns={columns}
			paginatedQuery={useAskForRegistrationUsers}
			w={["auto", "10rem"]}
			flexGrow={[1, 0]}
			cellLink={(cell) => {
				if (cell.id.endsWith("actions")) {
					return;
				}
				return `/contacts/users/${cell.row.id}`;
			}}
		></DataTable>
	);
};

function ContactPage() {
	const askForRegistrationModal = useDisclosure();
	const newEnterpriseModal = useDisclosure();
	const newCenterModal = useDisclosure();
	const tabRef = useRef<PersistingTabIndexHandler>(null);
	const enterpriseFormRef = useRef<EnterpriseFormHandler>(null);
	const centerFormRef = useRef<CenterFormHandler>(null);
	const queryClient = useQueryClient();
	const { mutate: mutateCreateEnterprise } = useMutation(createEnterprise, {
		onSuccess: () => {
			queryClient.invalidateQueries("enterprises");
			newEnterpriseModal.onClose();
		},
	});
	const { mutate: mutateCreateCenter } = useMutation(createCenter, {
		onSuccess: () => {
			queryClient.invalidateQueries("centers");
			newCenterModal.onClose();
		},
	});
	const handleAddButton = () => {
		if (tabRef.current) {
			switch (tabRef.current.getTab()) {
				case 0: {
					newEnterpriseModal.onOpen();
					break;
				}
				case 1: {
					newCenterModal.onOpen();
					break;
				}
			}
		}
	};
	const handleCreateEnterprise = async () => {
		if (enterpriseFormRef.current) {
			const valid = await enterpriseFormRef.current.valid();
			if (valid) {
				const value = enterpriseFormRef.current.values();
				mutateCreateEnterprise(value as Enterprise);
			}
		}
	};
	const handleCreateCenter = async () => {
		if (centerFormRef.current) {
			const valid = await centerFormRef.current.valid();
			if (valid) {
				const value = centerFormRef.current.values();
				mutateCreateCenter({ center: value });
			}
		}
	};
	const askRegistration = useAskForRegistrationUsers(
		1,
		1,
		undefined,
		undefined,
		undefined,
	);

	return (
		<>
			<Flex justifyContent={"space-between"}>
				<PageTitle>Contacts</PageTitle>
				<Flex gap={4}>
					<Button variant="outline" onClick={askForRegistrationModal.onOpen}>
						<AddUserIconGhost mr={2} />
						<Text
							lineHeight="21px"
							fontSize="14px"
							fontWeight="600"
							color="gray.800"
						>
							Demandes d'inscription
						</Text>
						<Badge
							lineHeight="21px"
							fontSize="14px"
							px="8px"
							ml="8px"
							backgroundColor="brand.50"
							color="brand.700"
							border={"solid 1px"}
							borderColor="brand.200"
							borderRadius="1000"
							textTransform="none"
						>
							{askRegistration?.meta.total}
						</Badge>
					</Button>
					<Button
						variant="solid"
						color="white"
						bgColor="brand.700"
						onClick={handleAddButton}
					>
						<AddIcon mr={4}></AddIcon>Ajouter
					</Button>
				</Flex>
			</Flex>

			<PersistingTabIndex ref={tabRef} id="contact" mt={6}>
				<TabList>
					<Tab _selected={{ color: "brand.700", borderColor: "brand.700" }}>
						Entreprises
					</Tab>
					<Tab _selected={{ color: "brand.700", borderColor: "brand.700" }}>
						Centres
					</Tab>
					<Tab _selected={{ color: "brand.700", borderColor: "brand.700" }}>
						Utilisateurs
					</Tab>
				</TabList>

				<TabPanels>
					<TabPanel px={0}>
						<ContactPageEnterprisesTab></ContactPageEnterprisesTab>
					</TabPanel>
					<TabPanel px={0}>
						<ContactPageCentersTab></ContactPageCentersTab>
					</TabPanel>
					<TabPanel px={0}>
						<ContactPageUsersTab></ContactPageUsersTab>
					</TabPanel>
				</TabPanels>
			</PersistingTabIndex>

			<SimpleModal
				w="60vw"
				title="Activation de comptes"
				icon={<AddUserIcon w="32px" h="32px"></AddUserIcon>}
				isOpen={askForRegistrationModal.isOpen}
				onClose={askForRegistrationModal.onClose}
			>
				<AskForRegistrationTable></AskForRegistrationTable>
			</SimpleModal>

			<SimpleModal
				title="Ajouter une entreprise"
				icon={<AddIcon color="brand.700" />}
				isOpen={newEnterpriseModal.isOpen}
				onClose={newEnterpriseModal.onClose}
				onClick={handleCreateEnterprise}
			>
				<EnterpriseForm ref={enterpriseFormRef} />
			</SimpleModal>

			<SimpleModal
				title="Ajouter un centre"
				icon={<AddIcon color="brand.700" />}
				isOpen={newCenterModal.isOpen}
				onClose={newCenterModal.onClose}
				onClick={handleCreateCenter}
			>
				<CenterForm ref={centerFormRef} />
			</SimpleModal>
		</>
	);
}

export default ContactPage;
